<template>
	<div class="c-listing-pagination__layout">
		<nav aria-label="Pagination">
			<ul class="c-listing-pagination">
				<!-- First Button -->
				<li :class="['c-listing-pagination__item c-listing-pagination__item--first', { 'is-disabled': currentPage === 1 }]">
					<button type="button"
							class="c-listing-pagination__item-button"
							@click="goToPage(1)"
							:disabled="currentPage === 1"
							aria-label="First page">
						<span aria-hidden="true">&laquo; First</span>
					</button>
				</li>

				<!-- Previous Button -->
				<li :class="['c-listing-pagination__item c-listing-pagination__item--prev', { 'is-disabled': currentPage === 1 }]">
					<button type="button"
							class="c-listing-pagination__item-button"
							@click="goToPage(currentPage - 1)"
							:disabled="currentPage === 1"
							aria-label="Previous page">
						<span aria-hidden="true">&lsaquo; Previous</span>
					</button>
				</li>

				<!-- Page Numbers with Ellipsis -->
				<div class="c-listing-pagination__numbers">
					<li v-for="(page, index) in pagesWithEllipsis"
						:key="index"
						:class="['c-listing-pagination__item c-listing-pagination__item--number', {
							'is-active': page === currentPage,
							'c-listing-pagination__item--ellipsis': page === '...'
						}]">
						<button v-if="page !== '...'"
								type="button"
								class="c-listing-pagination__item-button"
								@click="goToPage(page)"
								:aria-current="page === currentPage ? 'page' : null">
							<span class="visually-hidden">Page </span>{{ page }}
						</button>
						<span v-else class="c-listing-pagination__ellipsis" aria-hidden="true">...</span>
					</li>
				</div>

				<!-- Next Button -->
				<li :class="['c-listing-pagination__item c-listing-pagination__item--next', { 'is-disabled': currentPage === totalPages }]">
					<button type="button"
							class="c-listing-pagination__item-button"
							@click="goToPage(currentPage + 1)"
							:disabled="currentPage === totalPages"
							aria-label="Next page">
						<span aria-hidden="true">Next &rsaquo;</span>
					</button>
				</li>

				<!-- Last Button -->
				<li :class="['c-listing-pagination__item c-listing-pagination__item--last', { 'is-disabled': currentPage === totalPages }]">
					<button type="button"
							class="c-listing-pagination__item-button"
							@click="goToPage(totalPages)"
							:disabled="currentPage === totalPages"
							aria-label="Last page">
						<span aria-hidden="true">Last &raquo;</span>
					</button>
				</li>
			</ul>
		</nav>
	</div>
</template>

<script>
	export default {
		name: 'paginationModule',
		props: {
			currentPage: { type: Number, required: true },
			totalItems: { type: Number, required: true },
			pageSize: { type: Number, required: true }
		},
		computed: {
			totalPages() {
				return Math.ceil(this.totalItems / this.pageSize);
			},
			pagesWithEllipsis() {
				const total = this.totalPages;
				const current = this.currentPage;
				const maxVisible = 5; // Maximum number of page buttons to show

				if (total <= maxVisible + 2) {
					// Show all pages if total is small
					return Array.from({ length: total }, (_, i) => i + 1);
				}

				const pages = [];

				// Always show first page
				pages.push(1);

				// Calculate range around current page
				let startPage = Math.max(2, current - 1);
				let endPage = Math.min(total - 1, current + 1);

				// Adjust range if current is near start or end
				if (current <= 3) {
					endPage = Math.min(total - 1, maxVisible - 1);
				} else if (current >= total - 2) {
					startPage = Math.max(2, total - (maxVisible - 2));
				}

				// Add left ellipsis if needed
				if (startPage > 2) {
					pages.push('...');
				}

				// Add middle pages
				for (let i = startPage; i <= endPage; i++) {
					pages.push(i);
				}

				// Add right ellipsis if needed
				if (endPage < total - 1) {
					pages.push('...');
				}

				// Always show last page
				if (total > 1) {
					pages.push(total);
				}

				return pages;
			}
		},
		methods: {
			goToPage(page) {
				if (page < 1 || page > this.totalPages || page === this.currentPage) return;
				this.$emit('page-changed', page);
			}
		}
	};
</script>
